import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { StepperPageFooter } from "components/common/stepperPage";
import { getStepActions } from "./chartMindSteps";

/**
 * ChartMindFooter - step-aware navigation config for the shared sticky footer.
 *
 * Shows different actions based on current step with back/forward navigation.
 * When hasUnsavedChanges is true, center shows "Unsaved changes" instead of the last-saved timestamp.
 * The final (chart) step has Back only — no primary forward action in the footer.
 *
 * The bar itself is StepperPageFooter, shared with CareStart and VisitStart. This
 * file was the original; only the getButtonConfig switch is ChartMind-specific.
 */
const ChartMindFooter = ({
  currentStep,
  transcript,
  selectedDiagnosesCount,
  selectedConfirmedDiagnosesCount,
  onContinueToDiagnosis,
  onContinueToPearls = undefined,
  onContinueToPlan,
  onContinueToConfirm,
  onContinueToTreatment,
  onContinueToDischarge,
  onGenerateChart,
  onSkipToChart = undefined,
  onBack,
  saving,
  lastSavedAt,
  hasUnsavedChanges = false,
  // [BETA-KIJABE] When true, hides the autosave status indicator. Remove when beta ends.
  disableSave = false,
  // [BETA-CM0427] When true, Diagnosis's "Next" jumps straight to Treatment (Plan + Confirm hidden),
  // and Treatment/Discharge gate on selectedDiagnosesCount instead of confirmed. Remove when beta ends.
  cm0427Mode = false,
  inpatient = false,
  eventsNote = false,
  eventsCount = 0,
  pearlsAvailable = true,
  // Holds Next with a spinner, e.g. while a pasted transcript is still being clarified.
  nextLoading = false,
}) => {
  // Format last saved time
  const formatSavedTime = (date) => {
    if (!date) return null;
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };
  // ⚠️ THE SWITCH THIS REPLACED NOW LIVES IN chartMindSteps, because the footer is no longer
  // the only thing that can advance a step: a paired phone shows the same Next. Two copies of
  // the gating would eventually disagree, and the failure mode is a phone offering an action
  // the desktop refuses.
  const config = getStepActions({
    currentStep,
    transcript,
    selectedDiagnosesCount,
    selectedConfirmedDiagnosesCount,
    cm0427Mode,
    inpatient,
    eventsNote,
    eventsCount,
    pearlsAvailable,
    handlers: {
      onContinueToDiagnosis,
      onContinueToPearls,
      onContinueToPlan,
      onContinueToConfirm,
      onContinueToTreatment,
      onContinueToDischarge,
      onGenerateChart,
      onSkipToChart,
    },
  });

  // [BETA-KIJABE] Save status hidden for Kijabe beta testers — remove ternary when beta ends
  const saveStatus = disableSave ? null : saving ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <CircularProgress size={14} thickness={5} />
      <Typography variant="caption" color="text.secondary">
        Saving...
      </Typography>
    </Box>
  ) : hasUnsavedChanges ? (
    <Typography variant="caption" color="text.secondary">
      Unsaved changes
    </Typography>
  ) : lastSavedAt ? (
    <Typography variant="caption" color="text.secondary">
      Saved at {formatSavedTime(lastSavedAt)}
    </Typography>
  ) : null;

  return (
    <StepperPageFooter
      onBack={config.showBack ? onBack : undefined}
      primaryLabel={config.showNext ? config.nextLabel : undefined}
      onPrimary={config.onNext}
      primaryDisabled={config.nextDisabled}
      primaryLoading={nextLoading}
      secondaryLabel={config.secondaryLabel}
      onSecondary={config.onSecondary}
      secondaryDisabled={config.secondaryDisabled || nextLoading}
      status={saveStatus}
    />
  );
};

export default ChartMindFooter;
